import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { fetchCustomerDetail } from '#/api/crmMgmt/customer/fetchDetail';
import {
  fetchCustomerSelectOptions,
  mergeCustomerSelectOption,
} from '#/api/crmMgmt/customer/fetchSelectOptions';
import { createRepairRequest } from '#/api/aftersalesMgmt/repairRequest/create';
import { fetchRepairRequestDetail } from '#/api/aftersalesMgmt/repairRequest/fetchDetail';
import { fetchRepairRequestNextNo } from '#/api/aftersalesMgmt/repairRequest/fetchNextNo';
import { updateRepairRequest } from '#/api/aftersalesMgmt/repairRequest/update';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import LookupRefreshField from '#/modules/commonComponents/form/LookupRefreshField';
import FloatingSelect from '#/modules/commonComponents/form/select';
import AftersalesOpsPageHeader from '#/modules/crmMgmt/aftersales/components/AftersalesOpsPageHeader';
import { getCustomerApiErrorMessage } from '#/modules/crmMgmt/customer/helper/apiError';
import { formatCustomerAddressTypeLabel } from '#/modules/crmMgmt/customer/helper/customerAddress';
import RepairRequestEditReferenceSections from '#/modules/crmMgmt/aftersales/repairRequest/components/RepairRequestEditReferenceSections';
import RepairRequestLinesEditor from '#/modules/crmMgmt/aftersales/repairRequest/components/RepairRequestLinesEditor';
import RepairRequestStatusBadge from '#/modules/crmMgmt/aftersales/repairRequest/components/RepairRequestStatusBadge';
import {
  getRepairRequestApiErrorMessage,
  isRepairRequestNoDuplicated,
  isRepairRequestVersionConflict,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/apiError';
import {
  buildRepairRequestWritePayload,
  createEmptyRepairRequestLine,
  mapRepairRequestDetailToFormValues,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/formSerialize';
import {
  EMPTY_REPAIR_CONTACT_SNAPSHOT,
  EMPTY_REPAIR_CUSTOMER_SNAPSHOT,
  mapCustomerAddressToRepairAddress,
  mapCustomerContactToRepairSnapshot,
  mapCustomerDetailToRepairCustomerSnapshot,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/mapCustomerDetailToRepairSnapshot';
import { canEditRepairRequest } from '#/modules/crmMgmt/aftersales/repairRequest/helper/repairRequestStatus';
import {
  REPAIR_REQUEST_FIELD_LABELS,
  repairRequestFormSchema,
} from '#/modules/crmMgmt/aftersales/repairRequest/schemas/repairRequestSchema';
import CreateEditReferenceRail from '#/modules/infra/layout/CreateEditReferenceRail';
import PageShell from '#/modules/infra/layout/PageShell';
import { toast } from '#/utils/toast';
import {
  lookupQueryKey,
  repairRequestDetailQueryKey,
  repairRequestListQueryKeyPrefix,
} from '#/utils/queryKeys';

const DEFAULT_VALUES = {
  repairRequestNo: '',
  repairRequestDate: '',
  remark: '',
  customerId: '',
  contactId: '',
  customerName: '',
  contactName: '',
  phone: '',
  fax: '',
  taxId: '',
  addressId: '',
  address: '',
  receivedDate: '',
  lines: [createEmptyRepairRequestLine()],
};

const REPAIR_CUSTOMER_FIELD_KEYS = Object.keys(EMPTY_REPAIR_CUSTOMER_SNAPSHOT);
const REPAIR_CONTACT_FIELD_KEYS = Object.keys(EMPTY_REPAIR_CONTACT_SNAPSHOT);

function setSnapshotValues(setValue, keys, source) {
  keys.forEach((key) => {
    setValue(key, source[key] ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  });
}

/** 清空客戶區（客戶名稱／統編／傳真／地址）。 */
/** 報修單「選擇地址」選項：只顯示地址類型（詳細地址由下方「地址」欄帶入）。 */
function formatRepairAddressOptionLabel(row) {
  const text = formatCustomerAddressTypeLabel(row.addressType);
  return row.isDefault ? `${text}（預設）` : text;
}

/** 報修單「帶入聯絡人」選項：只顯示姓名與職務，不帶電話。 */
function formatRepairContactOptionLabel(contact) {
  const text = [contact.name, contact.title].filter(Boolean).join(' · ')
    || String(contact.id);
  return contact.isDefault ? `${text}（預設）` : text;
}

function clearRepairCustomerFields(setValue) {
  setSnapshotValues(
    setValue,
    REPAIR_CUSTOMER_FIELD_KEYS,
    EMPTY_REPAIR_CUSTOMER_SNAPSHOT,
  );
}

/** 清空聯絡人區（帶入聯絡人／聯絡人／電話）。 */
function clearRepairContactFields(setValue) {
  setSnapshotValues(
    setValue,
    REPAIR_CONTACT_FIELD_KEYS,
    EMPTY_REPAIR_CONTACT_SNAPSHOT,
  );
}

const SUB_GROUP_TITLE_CLASS = 'col-span-full mt-2 text-sm font-medium text-gray-600';

const CreateEditRepairRequestPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isCustomerDetailLoading, setIsCustomerDetailLoading] = useState(false);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [clearedDetailKey, setClearedDetailKey] = useState(null);
  const updatedAtRef = useRef(null);
  const customerIdRef = useRef('');
  /** 載入時的 customer_id；編輯只在變更時送出。 */
  const originalCustomerIdRef = useRef('');
  const customerDetailRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: repairRequestDetailQueryKey(id),
    queryFn: () => fetchRepairRequestDetail(id),
    enabled: Boolean(id),
  });

  const customerOptionsQuery = useQuery({
    queryKey: lookupQueryKey('customerSelect'),
    queryFn: fetchCustomerSelectOptions,
  });

  const nextNoQuery = useQuery({
    queryKey: ['erp', 'repairRequest', 'nextNo'],
    queryFn: () => fetchRepairRequestNextNo(),
    enabled: !id,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const suggestedNo = typeof nextNoQuery.data?.data === 'string'
    ? nextNoQuery.data.data
    : '';

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(repairRequestFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const watchedCustomerId = useWatch({
    control,
    name: 'customerId',
    defaultValue: '',
  });

  const linkedCustomer = detailQuery.data?.data?.customer;
  const customerOptions = useMemo(() => {
    const options = mergeCustomerSelectOption(customerOptionsQuery.data ?? [], linkedCustomer);
    return [{ value: '', label: '請選擇' }, ...options];
  }, [customerOptionsQuery.data, linkedCustomer]);

  const addressOptions = useMemo(() => {
    const options = (customerAddresses ?? [])
      .filter((row) => row?.id)
      .map((row) => ({
        value: row.id,
        label: formatRepairAddressOptionLabel(row),
      }));
    return [{ value: '', label: '請選擇' }, ...options];
  }, [customerAddresses]);

  const contactOptions = useMemo(() => {
    const options = (customerContacts ?? [])
      .filter((row) => row?.id)
      .map((row) => ({
        value: row.id,
        label: formatRepairContactOptionLabel(row),
      }));
    return [{ value: '', label: '請選擇' }, ...options];
  }, [customerContacts]);

  async function refreshNextNo() {
    const result = await nextNoQuery.refetch();
    const next = typeof result.data?.data === 'string' ? result.data.data : '';
    if (next) {
      setValue('repairRequestNo', next, { shouldValidate: true });
    }
    return next;
  }

  async function createWithDocumentNo(payload) {
    try {
      return await createRepairRequest(payload);
    } catch (error) {
      if (isRepairRequestNoDuplicated(error)) {
        await refreshNextNo();
        toast.info('單號已存在，已重新帶入建議單號，請確認後再儲存（勿自動重送）');
      }
      throw error;
    }
  }

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createWithDocumentNo,
    updateFn: updateRepairRequest,
    buildPayload: (values) => buildRepairRequestWritePayload(values, {
      updatedAt: id ? updatedAtRef.current : null,
      isEdit: Boolean(id),
      originalCustomerId: originalCustomerIdRef.current,
    }),
    listQueryKeyPrefix: repairRequestListQueryKeyPrefix,
    detailQueryKey: repairRequestDetailQueryKey,
    getDetailPath: ROUTES.repairRequestDetail,
    getErrorMessage: (error) => {
      if (error?.response?.data?.code === 'MATERIAL_SN_RETEST_REQUIRED') {
        const serial = error?.response?.data?.detail?.serialNumber
          || error?.response?.data?.detail?.serial_number
          || '';
        navigate(serial
          ? `${ROUTES.materialSn}?serial=${encodeURIComponent(serial)}`
          : ROUTES.materialSn);
      }
      return getRepairRequestApiErrorMessage(error, '儲存失敗');
    },
    isVersionConflict: isRepairRequestVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const detail = detailQuery.data?.data;
  const detailKey = detail
    ? `${id}:${detail.record?.updatedAt ?? ''}`
    : null;

  if (detailKey && detailKey !== clearedDetailKey) {
    setClearedDetailKey(detailKey);
    setCustomerContacts([]);
    setCustomerAddresses([]);
  }

  useEffect(() => {
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.record?.updatedAt ?? null;
    const formValues = mapRepairRequestDetailToFormValues(detail);
    customerIdRef.current = formValues.customerId;
    originalCustomerIdRef.current = formValues.customerId;
    customerDetailRef.current = null;
    resetValidationSummary();
    reset(formValues);

    if (!formValues.customerId) {
      return undefined;
    }
    // 已關聯客戶：只載入聯絡人／地址選項，不覆蓋單據快照
    let cancelled = false;
    fetchCustomerDetail(formValues.customerId)
      .then((result) => {
        const customerDetail = result?.data ?? null;
        if (cancelled || customerIdRef.current !== formValues.customerId) {
          return;
        }
        customerDetailRef.current = customerDetail;
        setCustomerContacts(customerDetail?.contacts ?? []);
        setCustomerAddresses(customerDetail?.addresses ?? []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [detail, reset, resetValidationSummary]);

  useEffect(() => {
    if (id || !suggestedNo) {
      return;
    }
    setValue('repairRequestNo', suggestedNo, { shouldValidate: false });
  }, [id, suggestedNo, setValue]);

  /** 選客戶：先清空客戶區＋聯絡人區，載入後帶入客戶名稱／統編／傳真／預設地址。 */
  async function handleCustomerChange(nextId) {
    const next = nextId ?? '';
    const prev = customerIdRef.current ?? '';
    if (next === prev) {
      return;
    }

    customerIdRef.current = next;
    customerDetailRef.current = null;
    setCustomerContacts([]);
    setCustomerAddresses([]);
    clearRepairCustomerFields(setValue);
    clearRepairContactFields(setValue);

    if (!next) {
      return;
    }

    setIsCustomerDetailLoading(true);
    try {
      const result = await fetchCustomerDetail(next);
      const customerDetail = result?.data ?? null;
      customerDetailRef.current = customerDetail;
      setCustomerContacts(customerDetail?.contacts ?? []);
      setCustomerAddresses(customerDetail?.addresses ?? []);
      setSnapshotValues(
        setValue,
        REPAIR_CUSTOMER_FIELD_KEYS,
        mapCustomerDetailToRepairCustomerSnapshot(customerDetail),
      );
    } catch (error) {
      toast.error(getCustomerApiErrorMessage(error, '載入客戶資料失敗'));
    } finally {
      setIsCustomerDetailLoading(false);
    }
  }

  /** 選地址：只覆蓋地址欄（仍可再手動修改）。 */
  function handleAddressChange(nextAddressId) {
    const line = mapCustomerAddressToRepairAddress(
      customerDetailRef.current,
      nextAddressId ?? '',
    );
    if (line == null) {
      return;
    }
    setValue('address', line, { shouldDirty: true, shouldValidate: true });
  }

  /** 選「帶入聯絡人」：只帶入聯絡人、電話。 */
  function handleContactChange(nextContactId) {
    const contactId = nextContactId ?? '';
    if (!contactId) {
      clearRepairContactFields(setValue);
      return;
    }

    setSnapshotValues(
      setValue,
      ['contactName', 'phone'],
      mapCustomerContactToRepairSnapshot(customerDetailRef.current, contactId),
    );
  }

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    if (!isEdit && !String(values.repairRequestNo ?? '').trim()) {
      toast.error('請先取得或填寫報修單號');
      return;
    }
    save(values);
  }

  const pageTitle = isEdit ? '編輯報修單' : '新增報修單';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const isReadOnlyEdit = isEdit && detail && !canEditRepairRequest(detail.status);
  const isNextNoLoading = !isEdit && nextNoQuery.isLoading;
  const isNextNoError = !isEdit && nextNoQuery.isError;
  const canShowForm = !isPageLoading && !isDetailNotFound && !isReadOnlyEdit;
  const canShowEditRail = isEdit && canShowForm && Boolean(detail);
  const hasCustomer = Boolean(watchedCustomerId);
  const isContactSelectDisabled = !hasCustomer || isCustomerDetailLoading;
  const isAddressSelectDisabled = !hasCustomer || isCustomerDetailLoading;

  const headerTrailing = isEdit && id ? (
    <Button type="button" variant="outline" asChild>
      <Link to={ROUTES.repairRequestDetail(id)}>返回詳情</Link>
    </Button>
  ) : null;

  const documentFields = (
    <section className="pb-6">
      <div className="form-block">
        <h2 className="form-block__title">單據資訊</h2>
      </div>
      <div
        className={cn(
          'grid grid-cols-1 gap-3',
          '@xl/sheet:grid-cols-2',
          '@4xl/sheet:grid-cols-4',
        )}
      >
        {!isEdit ? (
          <div className="col-span-1 @4xl/sheet:col-span-2 space-y-2">
            {isNextNoLoading ? (
              <p className="text-sm text-slate-500">取得建議單號中…</p>
            ) : null}
            {isNextNoError ? (
              <p className="text-sm text-destructive">
                {getRepairRequestApiErrorMessage(
                  nextNoQuery.error,
                  '取得建議單號失敗',
                )}
              </p>
            ) : null}
            <LookupRefreshField
              onRefresh={() => refreshNextNo()}
              isRefreshing={nextNoQuery.isFetching}
              ariaLabel={
                isNextNoError ? '重新取得報修單號' : '重新建議報修單號'
              }
            >
              <FloatingInput
                id="repairRequestNo"
                label="報修單號"
                register={register}
                errors={errors}
              />
            </LookupRefreshField>
          </div>
        ) : (
          <FloatingInput
            id="repairRequestNo"
            label="報修單號"
            value={detail?.repairRequestNo ?? ''}
            readOnly
            containerClassName="col-span-1 @4xl/sheet:col-span-2"
          />
        )}
        <FloatingDateInput
          id="repairRequestDate"
          label="報修日"
          control={control}
          errors={errors}
          placeholder="西元年/月/日"
        />
        <FloatingDateInput
          id="receivedDate"
          label="收件日"
          control={control}
          errors={errors}
          placeholder="西元年/月/日"
        />

        <p className={SUB_GROUP_TITLE_CLASS}>客戶資料</p>
        <FloatingSelect
          id="customerId"
          label="客戶"
          options={customerOptions}
          control={control}
          errors={errors}
          isDisabled={customerOptionsQuery.isLoading || isCustomerDetailLoading}
          noOptionsMessage="無可用客戶"
          onChange={handleCustomerChange}
        />
        <FloatingInput
          id="customerName"
          label="客戶名稱"
          register={register}
          errors={errors}
        />
        <FloatingInput
          id="taxId"
          label="統一編號"
          register={register}
          errors={errors}
        />
        <FloatingInput
          id="fax"
          label="傳真"
          register={register}
          errors={errors}
        />
        <FloatingSelect
          id="addressId"
          label="選擇地址"
          options={addressOptions}
          control={control}
          errors={errors}
          isDisabled={isAddressSelectDisabled}
          noOptionsMessage={hasCustomer ? '此客戶尚無地址' : '請先選擇客戶'}
          tip={hasCustomer ? undefined : '請先選擇客戶，才能選擇地址'}
          onChange={handleAddressChange}
          containerClassName="col-span-1 @xl/sheet:col-span-2 @4xl/sheet:col-span-1"
        />
        <FloatingInput
          id="address"
          label="地址"
          register={register}
          errors={errors}
          containerClassName="col-span-1 @xl/sheet:col-span-2 @4xl/sheet:col-span-3"
        />

        <p className={SUB_GROUP_TITLE_CLASS}>聯絡人</p>
        <FloatingSelect
          id="contactId"
          label="帶入聯絡人"
          options={contactOptions}
          control={control}
          errors={errors}
          isDisabled={isContactSelectDisabled}
          noOptionsMessage={hasCustomer ? '此客戶尚無聯絡人' : '請先選擇客戶'}
          onChange={handleContactChange}
          containerClassName="col-span-1 @xl/sheet:col-span-2"
        />
        <FloatingInput
          id="contactName"
          label="聯絡人"
          register={register}
          errors={errors}
        />
        <FloatingInput
          id="phone"
          label="電話"
          register={register}
          errors={errors}
        />

        <FloatingTextarea
          id="remark"
          label="備註"
          register={register}
          errors={errors}
          rows={2}
          containerClassName="col-span-1 @xl/sheet:col-span-2 @4xl/sheet:col-span-4"
        />
      </div>
    </section>
  );

  const formActions = (
    <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
      <Button type="button" variant="outline" asChild>
        <Link to={id ? ROUTES.repairRequestDetail(id) : ROUTES.repairRequest}>
          取消
        </Link>
      </Button>
      <Button
        type="submit"
        disabled={isPending || (!isEdit && isNextNoLoading)}
      >
        {isPending ? '儲存中…' : '儲存'}
      </Button>
    </div>
  );

  const formInner = (
    <>
      {documentFields}
      <RepairRequestLinesEditor
        control={control}
        register={register}
        setValue={setValue}
        errors={errors}
        watch={watch}
        fillWarrantyFromSalesDate={!isEdit}
      />
    </>
  );

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div
          className={cn(
            isEdit
              ? 'flex min-h-0 w-full flex-1 flex-col'
              : 'mx-auto w-full max-w-6xl',
          )}
        >
          <AftersalesOpsPageHeader
            page="repairRequest"
            variant="form"
            title={pageTitle}
            trailing={headerTrailing}
          />

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getRepairRequestApiErrorMessage(detailQuery.error, '找不到報修單')}
            </p>
          ) : null}

          {isReadOnlyEdit ? (
            <div className="space-y-3">
              <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>僅草稿可編輯。目前狀態為</span>
                <RepairRequestStatusBadge status={detail.status} size="sm" />
                <span>，請至詳情頁操作。</span>
              </p>
              <Button type="button" asChild>
                <Link to={ROUTES.repairRequestDetail(id)}>返回詳情</Link>
              </Button>
            </div>
          ) : null}

          {canShowEditRail ? (
            <CreateEditReferenceRail
              className="min-h-0 flex-1"
              main={(
                <>
                  <FormValidationSummary
                    show={isSubmitAttempted}
                    errors={errors}
                    fieldLabels={REPAIR_REQUEST_FIELD_LABELS}
                    customMessages={customMessages}
                  />
                  <form
                    className="sheet create_edit create_edit_form create_edit_form--fill"
                    onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                    noValidate
                  >
                    <div className="create_edit_form__body divide-y divide-gray-500">
                      {formInner}
                    </div>
                    {formActions}
                  </form>
                </>
              )}
              reference={<RepairRequestEditReferenceSections detail={detail} />}
            />
          ) : null}

          {!isEdit && canShowForm ? (
            <form
              className="sheet create_edit create_edit_form create_edit_form--page-scroll"
              onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
              noValidate
            >
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={REPAIR_REQUEST_FIELD_LABELS}
                customMessages={customMessages}
                className="mt-5"
              />
              <div className="create_edit_form__body divide-y divide-gray-500">
                {formInner}
              </div>
              {formActions}
            </form>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditRepairRequestPage;
